import { VpDto } from "../api/Api";
import {
  APP_ROLES,
  AppRole,
  LifecycleName,
  LIFECYCLE_ACCESS_ROLES
} from "./roles";

export type AppRoleSet = ReadonlySet<AppRole>;

const publicLifecycles = new Set<LifecycleName>(["tech"]);

export const hasAnyAppRole = (roles: AppRoleSet) => roles.size > 0;

export const isGlobalLifecycleRole = (roles: AppRoleSet) =>
  roles.has(APP_ROLES.Admin) || roles.has(APP_ROLES.MarketAccess);

/**
 * Mergers are run by Compliance rather than Market Access, so on a merger ComplianceLegal holds
 * the rights the global role holds everywhere: create, edit, every lifecycle, sign-off, deleting
 * attachments. Mirrors the backend's ClaimsHelper.CanManage. Pass the entity's isMerger (or
 * useIsEntityMerger() inside a detail page); everything else defaults to the global role only.
 */
export const canManageEntity = (roles: AppRoleSet, isMerger = false) =>
  isGlobalLifecycleRole(roles) ||
  (isMerger && roles.has(APP_ROLES.ComplianceLegal));

export const canCreateMerger = (roles: AppRoleSet) =>
  canManageEntity(roles, true);

// Is this user the VP named on this specific entity? Mirrors isSignatoryEmail: a VP holds no
// app role, so they are indistinguishable from a plain trader in the token and the signal has
// to come from the entity itself. Never express VP access in AppRoleSet terms, or every
// grant-mode trader gets it too.
export const isEntityVpEmail = (
  vp: VpDto | null | undefined,
  email: string | null | undefined
) => !!email && !!vp?.email && vp.email.toLowerCase() === email.toLowerCase();

export const canAccessLifecycle = (
  roles: AppRoleSet,
  lifecycle: LifecycleName,
  isEntityVp = false,
  isMerger = false
) =>
  isEntityVp ||
  publicLifecycles.has(lifecycle) ||
  canManageEntity(roles, isMerger) ||
  roles.has(LIFECYCLE_ACCESS_ROLES[lifecycle]);

/**
 * Whether the caller is the department that owns this lifecycle, as opposed to someone who merely
 * has access to it.
 *
 * This is what decides if a save moves the progress circle to "in progress". Market Access and Admin
 * can open every lifecycle, and they routinely do so to assign the department that will actually do
 * the work. That assignment is not the department starting: showing it as in progress makes the
 * overview claim work is underway when nobody has picked it up yet.
 *
 * Deliberately does not include the global roles, unlike canAccessLifecycle. Market Access owns the
 * market access lifecycle through the mapping like any other department, and nothing else.
 */
export const ownsLifecycle = (roles: AppRoleSet, lifecycle: LifecycleName) =>
  roles.has(LIFECYCLE_ACCESS_ROLES[lifecycle]);

// A VP reads every lifecycle but writes none of them, so the lifecycle Save, Complete and
// comment controls are hidden for them.
export const canEditLifecycle = (roles: AppRoleSet, isEntityVp = false) =>
  !isEntityVp && hasAnyAppRole(roles);

// The hidden progress dropdown in every lifecycle dialog, which sets a lifecycle's progress directly
// without Save or Complete. Global role only, also on a merger.
export const canOverrideLifecycleProgress = (roles: AppRoleSet) =>
  isGlobalLifecycleRole(roles);

export const canCreateCounterparty = (roles: AppRoleSet) =>
  roles.has(APP_ROLES.Admin) || roles.has(APP_ROLES.MarketAccess);

export const canUseOnboardingRequests = (roles: AppRoleSet) =>
  !hasAnyAppRole(roles) || isGlobalLifecycleRole(roles);

export const canEditCounterparty = (roles: AppRoleSet, isMerger = false) =>
  canManageEntity(roles, isMerger);

// The bulk warehouse sync page writes credit and KYC values across every counterparty at once, so
// it is not canEditCounterparty (Market Access + Admin): the departments that own those fields are
// Compliance and Risk, and they are the ones who asked for it.
export const canBulkSyncWarehouse = (roles: AppRoleSet) =>
  isGlobalLifecycleRole(roles) ||
  roles.has(APP_ROLES.ComplianceLegal) ||
  roles.has(APP_ROLES.Risk);

export const canManageTraderAccess = (roles: AppRoleSet) =>
  isGlobalLifecycleRole(roles);

export const canManageSignOff = (roles: AppRoleSet, isMerger = false) =>
  canManageEntity(roles, isMerger);

// Sign-off reject + comments are visible/writable to the signatory VPs plus the global
// roles (Market Access, Admin), and ComplianceLegal on a merger. Pass whether the current user
// is on the signatory list.
export const canParticipateInSignOff = (
  roles: AppRoleSet,
  isSignatory: boolean,
  isMerger = false
) => canManageSignOff(roles, isMerger) || isSignatory;

// Brokers reuse the same role model as counterparties (no Treasury/Tech lifecycles).
export const canCreateBroker = (roles: AppRoleSet) =>
  roles.has(APP_ROLES.Admin) || roles.has(APP_ROLES.MarketAccess);

export const canEditBroker = (roles: AppRoleSet, isMerger = false) =>
  canManageEntity(roles, isMerger);

export const canManageBrokerOnboarding = (roles: AppRoleSet) =>
  isGlobalLifecycleRole(roles);

export const canAccessBrokerLifecycle = (
  roles: AppRoleSet,
  lifecycle: LifecycleName,
  isEntityVp = false,
  isMerger = false
) => canAccessLifecycle(roles, lifecycle, isEntityVp, isMerger);

// Country additions reuse the counterparty role model unchanged: same seven lifecycles, same owners.
export const canCreateCountry = (roles: AppRoleSet) =>
  roles.has(APP_ROLES.Admin) || roles.has(APP_ROLES.MarketAccess);

export const canEditCountry = (roles: AppRoleSet) =>
  roles.has(APP_ROLES.Admin) || roles.has(APP_ROLES.MarketAccess);

export const canCommentCountry = (roles: AppRoleSet) => hasAnyAppRole(roles);

export const canManageCountryOnboarding = (roles: AppRoleSet) =>
  isGlobalLifecycleRole(roles);

export const canAccessCountryLifecycle = (
  roles: AppRoleSet,
  lifecycle: LifecycleName,
  isEntityVp = false
) => canAccessLifecycle(roles, lifecycle, isEntityVp);
